/**
 * SiteLayerEditor — modal map + slide-over panel for adding, editing,
 * and deleting `site.siteLayers` (KMZ + manual overlays). Site boundary
 * is shown for context; edit it via the Boundary editor.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import {
  Hexagon,
  Download,
  FileUp,
  Layers,
  Loader2,
  LockKeyhole,
  Pencil,
  PlugZap,
  Scissors,
  Spline,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { openConfirmDialog } from '../ui/confirmDialog';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  colorForCategory,
  LAYER_COLORS,
} from '../../config/siteLayerColors';
import {
  createManualLayer,
  defaultNewLayerName,
  mergeLayerListsForSave,
  normalizeLayerColor,
  patchSiteLayer,
  removeSiteLayer,
  LAYER_COLOR_PRESETS,
} from '../../lib/siteLayerEditor';
import {
  cadGeoreferenceAtSiteAnchor,
  cadImportToSiteLayers,
  defaultCadGeoreference,
  mergeCadImportedLayers,
} from '../../lib/cad/cadImport';
import { importDwgFile } from '../../services/cadImport';
import { downloadCadLayersDxf } from '../../services/cadDxfExporter';
import {
  countLayersByType,
  partitionLayersByBoundaries,
  partitionLayersByRadius,
  partitionLayersByType,
  radiusToMeters,
  type RadiusUnit,
} from '../../lib/siteLayerScope';
import {
  isManagedGenerationImpactLayer,
  preserveManagedGenerationImpactLayers,
} from '../../lib/siteLayers/managedGenerationImpactLayers';
import { isSiteLayerSetWriteReady } from '../../lib/siteLayers/canonicalLayerResolution';
import { useStore } from '../../store';
import { materializeSiteBoundaries } from '../../lib/siteBoundaryGeometry';
import { emitAuditEvent } from '../../lib/auditLogClient';
import { landGateReviewTransitions } from '../../lib/siteLayers/landGatePromotion';
import {
  fetchLandGateSiteLayers,
  isLandGateImportEnabled,
  mergeLandGateLayers,
} from '../../services/landgate/landGateMapData';
import {
  LANDGATE_CONNECTION_CHANGED_EVENT,
  LandGateConnectionError,
  requestLandGateConnectionDialog,
} from '../../services/landgate/landGateConnectionClient';
import type { LandGateConnectionMetadata } from '../../services/landgate/landGateConnectionTypes';
import type { SiteEntity, SiteLayer, SiteLayerCategory } from '../../types/site';
import type { CadGeoreference, CadImportPayload } from '../../types/cad';
import { FitBoundsOnMount } from './FitBoundsOnMount';
import { SiteBoundaryOverlay, SiteLayerOverlays } from './SiteMapLayers';
import {
  SiteLayerDrawOverlay,
  type DrawToolMode,
} from './SiteLayerDrawOverlay';
import { LandGatePromotionControls } from './LandGatePromotionControls';

export interface SiteLayerEditorProps {
  site: SiteEntity;
  open: boolean;
  onClose: () => void;
}

interface LayerDraftHistoryEntry {
  layers: SiteLayer[];
  baselineIds: Set<string>;
}

const DIALOG_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const MAX_SITE_LAYERS_AFTER_CAD_IMPORT = 450;
const MAX_CAD_ENTITIES_PER_IMPORT = 400;

// Google Maps needs concrete colors rather than CSS custom properties. Keep
// the established site-marker/boundary palette named here so JSX never relies
// on unexplained color literals.
const SITE_MAP_COLORS = {
  markerBackground: '#D97706',
  markerGlyph: '#ffffff',
  markerBorder: '#92400e',
  boundaryStroke: '#D97706',
} as const;

export const SiteLayerEditor: React.FC<SiteLayerEditorProps> = ({
  site,
  open,
  onClose,
}) => {
  const updateSiteLayers = useStore(s => s.updateSiteLayers);
  const [draftLayers, setDraftLayers] = useState<SiteLayer[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState<DrawToolMode>(null);
  const [pendingGeometryType, setPendingGeometryType] = useState<'polygon' | 'line' | null>(null);
  const [saving, setSaving] = useState(false);
  const [landGateLoading, setLandGateLoading] = useState(false);
  const [landGateConnectionRequired, setLandGateConnectionRequired] = useState(false);
  // In-dialog undo stack — each bulk/scope op pushes the pre-change draft
  // snapshot + its baseline membership here so the user can revert before
  // SAVE LAYERS without treating canonical layers hydrated after the snapshot
  // as implicit deletions. Everything stays staged; CANCEL discards the whole
  // draft, UNDO pops one step.
  const [undoStack, setUndoStack] = useState<LayerDraftHistoryEntry[]>([]);
  const undoStackRef = useRef<LayerDraftHistoryEntry[]>([]);
  undoStackRef.current = undoStack;
  // Radius-scope toolbar inputs (staged action; not persisted).
  const [radiusValue, setRadiusValue] = useState('5');
  const [radiusUnit, setRadiusUnit] = useState<RadiusUnit>('km');
  const [cadLoading, setCadLoading] = useState(false);
  const [cadExporting, setCadExporting] = useState(false);
  const [cadPayload, setCadPayload] = useState<CadImportPayload | null>(null);
  const [cadAlignment, setCadAlignment] = useState<CadGeoreference | null>(null);
  const [selectedCadLayers, setSelectedCadLayers] = useState<Set<string>>(new Set());
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cadInputRef = useRef<HTMLInputElement | null>(null);
  const draftLayersRef = useRef(draftLayers);
  const draftRevisionRef = useRef(site.siteLayersRevision ?? 0);
  const draftBaselineIdsRef = useRef<Set<string>>(new Set());
  const activeSiteIdRef = useRef(site.id);
  const landGateRequestRef = useRef<{
    siteId: string;
    controller: AbortController;
  } | null>(null);
  const cadRequestRef = useRef<{
    siteId: string;
    controller: AbortController;
  } | null>(null);
  draftLayersRef.current = draftLayers;
  activeSiteIdRef.current = site.id;

  // R-131-2 — only reset the draft on the close→open transition or on a
  // genuine site switch. Listing `site.siteLayers` in the dep array (pre-
  // R-131-2 wiring) re-ran the effect every time the underlying store
  // re-projected siteLayers — including the optimistic + post-Firestore
  // double `set` inside `updateSiteLayers` — which clobbered any in-flight
  // edits the user had made since opening the editor. The lastOpenedRef
  // tracks "we already initialised this editor instance" so the reset
  // fires exactly once per open cycle.
  const lastOpenedRef = useRef<{ open: boolean; siteId: string | null }>({
    open: false,
    siteId: null,
  });
  useEffect(() => {
    if (!open) {
      landGateRequestRef.current?.controller.abort();
      landGateRequestRef.current = null;
      cadRequestRef.current?.controller.abort();
      cadRequestRef.current = null;
      setLandGateLoading(false);
      setCadLoading(false);
      lastOpenedRef.current = { open: false, siteId: null };
      return;
    }
    const prev = lastOpenedRef.current;
    const isOpenTransition = !prev.open;
    const isSiteSwitch = prev.siteId !== null && prev.siteId !== site.id;
    if (!isOpenTransition && !isSiteSwitch) return;
    landGateRequestRef.current?.controller.abort();
    landGateRequestRef.current = null;
    cadRequestRef.current?.controller.abort();
    cadRequestRef.current = null;
    const initialLayers = site.siteLayers ?? [];
    draftRevisionRef.current = site.siteLayersRevision ?? 0;
    draftBaselineIdsRef.current = new Set(initialLayers.map((layer) => layer.id));
    draftLayersRef.current = initialLayers;
    setDraftLayers(initialLayers);
    setSelectedId(null);
    setDrawMode(null);
    setPendingGeometryType(null);
    setSaving(false);
    setLandGateLoading(false);
    setCadLoading(false);
    setCadExporting(false);
    setCadPayload(null);
    setCadAlignment(null);
    setSelectedCadLayers(new Set());
    undoStackRef.current = [];
    setUndoStack([]);
    lastOpenedRef.current = { open: true, siteId: site.id };
  // Intentionally omit `site.siteLayers` — re-hydrating on every store
  // projection clobbered in-flight category/name edits (R-131-2).
  }, [open, site.id]);

  // If the editor was opened before Phase-2 canonical layer hydration finished
  // on the same site revision, rebase newly hydrated canonical layers into the
  // draft, baseline ID set, and any existing Undo snapshots without clobbering
  // in-flight user edits or intentional deletions of baseline layers.
  useEffect(() => {
    if (!open) return;
    if (lastOpenedRef.current.siteId !== site.id) return;
    if ((site.siteLayersRevision ?? 0) !== draftRevisionRef.current) return;
    const incoming = site.siteLayers ?? [];
    if (incoming.length === 0) return;
    const baselineIds = draftBaselineIdsRef.current;
    const currentDraft = draftLayersRef.current;
    const currentDraftIds = new Set(currentDraft.map((layer) => layer.id));
    const newlyHydrated = incoming.filter(
      (layer) => !baselineIds.has(layer.id) && !currentDraftIds.has(layer.id),
    );
    if (newlyHydrated.length > 0) {
      for (const layer of newlyHydrated) {
        baselineIds.add(layer.id);
      }
      const nextDraft = [...currentDraft, ...newlyHydrated];
      draftLayersRef.current = nextDraft;
      setDraftLayers(nextDraft);
    }
    if (undoStackRef.current.length > 0) {
      let stackChanged = false;
      const rebasedStack = undoStackRef.current.map((entry) => {
        const entryLayerIds = new Set(entry.layers.map((layer) => layer.id));
        const missingInEntry = incoming.filter(
          (layer) => !entry.baselineIds.has(layer.id) && !entryLayerIds.has(layer.id),
        );
        if (missingInEntry.length === 0) return entry;
        stackChanged = true;
        const nextEntryBaseline = new Set(entry.baselineIds);
        for (const layer of missingInEntry) {
          nextEntryBaseline.add(layer.id);
        }
        return {
          layers: [...entry.layers, ...missingInEntry],
          baselineIds: nextEntryBaseline,
        };
      });
      if (stackChanged) {
        undoStackRef.current = rebasedStack;
        setUndoStack(rebasedStack);
      }
    }
  }, [open, site.id, site.siteLayers, site.siteLayersRevision]);

  useEffect(() => () => {
    landGateRequestRef.current?.controller.abort();
    landGateRequestRef.current = null;
    cadRequestRef.current?.controller.abort();
    cadRequestRef.current = null;
  }, []);

  useEffect(() => {
    const handleConnectionChanged = (event: Event) => {
      const connection = (event as CustomEvent<{
        connection?: LandGateConnectionMetadata;
      }>).detail?.connection;
      setLandGateConnectionRequired(
        connection?.state === 'not-configured' || connection?.state === 'needs-attention',
      );
    };
    window.addEventListener(LANDGATE_CONNECTION_CHANGED_EVENT, handleConnectionChanged);
    return () => window.removeEventListener(LANDGATE_CONNECTION_CHANGED_EVENT, handleConnectionChanged);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    dialogRef.current?.focus();
    return () => {
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [open]);

  const selectedLayer = useMemo(
    () => draftLayers.find(l => l.id === selectedId) ?? null,
    [draftLayers, selectedId],
  );
  const selectedLayerManaged = selectedLayer != null
    && isManagedGenerationImpactLayer(selectedLayer);
  const siteParcels = useMemo(() => materializeSiteBoundaries({
    siteBoundaries: site.siteBoundaries,
    siteBoundary: site.siteBoundary,
    siteArea: site.siteArea,
    sitePerimeter: site.sitePerimeter,
    siteCentroid: site.siteCentroid,
  }), [
    site.siteArea,
    site.siteBoundaries,
    site.siteBoundary,
    site.siteCentroid,
    site.sitePerimeter,
  ]);

  const selectedColor = selectedLayer
    ? normalizeLayerColor(selectedLayer.color, selectedLayer.category)
    : LAYER_COLORS.other;

  const startAddLayer = (geometryType: 'polygon' | 'line') => {
    setPendingGeometryType(geometryType);
    setSelectedId(null);
    setDrawMode(geometryType);
  };

  const startEditGeometry = () => {
    if (!selectedLayer) return;
    if (selectedLayerManaged) {
      toast.info('Published Generation & Impact layers are managed from the Generation & Impact workspace.');
      return;
    }
    const mode: DrawToolMode =
      selectedLayer.geometry.type === 'point'
        ? 'edit-point'
        : selectedLayer.geometry.type === 'line'
          ? 'edit-line'
          : 'edit-polygon';
    setDrawMode(mode);
  };

  const handleDrawComplete = useCallback((coordinates: { lat: number; lng: number }[]) => {
    if (drawMode === 'edit-polygon' || drawMode === 'edit-line' || drawMode === 'edit-point') {
      if (!selectedId) return;
      const selected = draftLayersRef.current.find((layer) => layer.id === selectedId);
      if (selected && isManagedGenerationImpactLayer(selected)) {
        setDrawMode(null);
        toast.info('Published Generation & Impact geometry is read-only here.');
        return;
      }
      setDraftLayers(prev => patchSiteLayer(prev, selectedId, {
        geometry: {
          type: drawMode === 'edit-point' ? 'point' : drawMode === 'edit-line' ? 'line' : 'polygon',
          coordinates,
        },
      }));
      setDrawMode(null);
      return;
    }

    const geometryType = pendingGeometryType ?? 'polygon';
    const layer = createManualLayer({
      name: defaultNewLayerName(geometryType, draftLayers.length),
      category: 'other',
      geometryType,
      coordinates,
    });
    setDraftLayers(prev => [...prev, layer]);
    setSelectedId(layer.id);
    setDrawMode(null);
    setPendingGeometryType(null);
  }, [drawMode, pendingGeometryType, selectedId, draftLayers.length]);

  const handleDrawCancel = () => {
    setDrawMode(null);
    setPendingGeometryType(null);
  };

  /**
   * Stage a bulk change: snapshot the current draft onto the undo stack,
   * then swap in `next`. No store write — everything stays staged until
   * SAVE LAYERS (CANCEL discards, UNDO pops). Selection is cleared if the
   * selected layer was removed.
   */
  const applyBulk = (next: SiteLayer[], previous = draftLayers) => {
    const protectedNext = preserveManagedGenerationImpactLayers(previous, next);
    const snapshotEntry: LayerDraftHistoryEntry = {
      layers: previous,
      baselineIds: new Set(draftBaselineIdsRef.current),
    };
    const nextUndoStack = [...undoStackRef.current, snapshotEntry];
    undoStackRef.current = nextUndoStack;
    setUndoStack(nextUndoStack);
    draftLayersRef.current = protectedNext;
    setDraftLayers(protectedNext);
    if (selectedId && !protectedNext.some(l => l.id === selectedId)) {
      setSelectedId(null);
    }
  };

  const handleUndo = () => {
    if (undoStackRef.current.length === 0) return;
    const restoredEntry = undoStackRef.current[undoStackRef.current.length - 1]!;
    const sameRevisionIncoming =
      (site.siteLayersRevision ?? 0) === draftRevisionRef.current
        ? (site.siteLayers ?? [])
        : [];
    const restoredLayerIds = new Set(restoredEntry.layers.map((layer) => layer.id));
    const newlyArrived = sameRevisionIncoming.filter(
      (layer) => !restoredEntry.baselineIds.has(layer.id) && !restoredLayerIds.has(layer.id),
    );
    const nextLayers =
      newlyArrived.length > 0
        ? [...restoredEntry.layers, ...newlyArrived]
        : restoredEntry.layers;
    const nextBaseline = new Set(restoredEntry.baselineIds);
    for (const layer of newlyArrived) {
      nextBaseline.add(layer.id);
    }
    draftBaselineIdsRef.current = nextBaseline;
    draftLayersRef.current = nextLayers;
    setDraftLayers(nextLayers);
    const poppedStack = undoStackRef.current.slice(0, -1);
    undoStackRef.current = poppedStack;
    setUndoStack(poppedStack);
    setSelectedId(null);
  };

  const editableDraftLayers = useMemo(
    () => draftLayers.filter((layer) => !isManagedGenerationImpactLayer(layer)),
    [draftLayers],
  );
  const managedLayerCount = draftLayers.length - editableDraftLayers.length;
  const layerTypeCounts = useMemo(
    () => countLayersByType(editableDraftLayers),
    [editableDraftLayers],
  );
  const editableCadLayerCount = useMemo(
    () => draftLayers.filter(layer => layer.source === 'cad-import').length,
    [draftLayers],
  );
  const selectedCadFeatureCount = useMemo(() => {
    if (!cadPayload) return 0;
    return cadPayload.cadLayers.reduce(
      (total, layer) => total + (selectedCadLayers.has(layer.name) ? layer.featureCount : 0),
      0,
    );
  }, [cadPayload, selectedCadLayers]);

  const handleDeleteAll = async () => {
    if (editableDraftLayers.length === 0) return;
    const managedNote = managedLayerCount > 0
      ? ` ${managedLayerCount} published Generation & Impact layer${managedLayerCount === 1 ? '' : 's'} will remain protected.`
      : '';
    const ok = await openConfirmDialog({
      title: 'Delete all layers?',
      body: `Remove all ${editableDraftLayers.length} editable layer${editableDraftLayers.length === 1 ? '' : 's'} from this site?${managedNote} This is staged — Undo or Cancel reverts it; Save Layers commits it.`,
      confirmLabel: 'Delete all',
      cancelLabel: 'Keep layers',
      tone: 'danger',
    });
    if (!ok) return;
    const removed = editableDraftLayers.length;
    applyBulk([]);
    toast.success(`Removed ${removed} layer${removed === 1 ? '' : 's'} (staged) · Undo to revert`);
  };

  const handleDeleteByType = (type: SiteLayer['geometry']['type']) => {
    const { match, rest } = partitionLayersByType(editableDraftLayers, type);
    if (match.length === 0) return;
    applyBulk(rest);
    const label = type === 'polygon' ? 'area' : type;
    toast.success(`Removed ${match.length} ${label} layer${match.length === 1 ? '' : 's'} (staged) · Undo to revert`);
  };

  const scopeCenter = site.siteCentroid ?? site.location ?? null;

  const handleKeepWithinRadius = () => {
    if (!scopeCenter) {
      toast.error('No site centre available to measure a radius from.');
      return;
    }
    const value = Number(radiusValue);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a radius greater than zero.');
      return;
    }
    const meters = radiusToMeters(value, radiusUnit);
    const { keep, drop, unlocatable } = partitionLayersByRadius(editableDraftLayers, scopeCenter, meters);
    if (drop.length === 0) {
      toast.info(`No layers fall outside ${value} ${radiusUnit} — nothing removed.`);
      return;
    }
    applyBulk(keep);
    const note = unlocatable.length > 0 ? ` (${unlocatable.length} kept — no coordinate)` : '';
    toast.success(`Kept ${keep.length}, removed ${drop.length} beyond ${value} ${radiusUnit}${note} · Undo to revert`);
  };

  const handleKeepWithinBoundary = () => {
    const parcelBoundaries = siteParcels.map(parcel => parcel.polygon);
    if (parcelBoundaries.length === 0) {
      toast.error('Draw or import a site boundary first (Boundary button on overview).');
      return;
    }
    const { keep, drop, unlocatable } = partitionLayersByBoundaries(editableDraftLayers, parcelBoundaries);
    if (drop.length === 0) {
      toast.info('No layers fall outside the site boundary — nothing removed.');
      return;
    }
    applyBulk(keep);
    const note = unlocatable.length > 0 ? ` (${unlocatable.length} kept — no coordinate)` : '';
    toast.success(`Kept ${keep.length}, removed ${drop.length} outside boundary${note} · Undo to revert`);
  };

  const handleSave = async () => {
    if (!isSiteLayerSetWriteReady(site)) {
      toast.error(
        site._siteLayersHydrationError
          ? 'Site layers failed to load completely. Refresh the site before modifying layers.'
          : 'Site layers are still loading. Wait for layer hydration to finish before modifying layers.',
      );
      return;
    }
    setSaving(true);
    try {
      const sameRevisionPrevious =
        (site.siteLayersRevision ?? 0) === draftRevisionRef.current
          ? site.siteLayers
          : undefined;
      const merged = mergeLayerListsForSave(
        sameRevisionPrevious,
        draftLayersRef.current,
        draftBaselineIdsRef.current,
      );
      const reviewTransitions = landGateReviewTransitions(site.siteLayers, merged);
      await updateSiteLayers(site.id, merged, draftRevisionRef.current);
      for (const transition of reviewTransitions) {
        void emitAuditEvent({
          action: transition.action === 'promote'
            ? 'landgate.layer_promoted'
            : 'landgate.layer_unpromoted',
          target: { type: 'other', id: transition.layerId, label: transition.layerName },
          before: { use: transition.action === 'promote' ? 'map-only' : 'screening' },
          after: { use: transition.action === 'promote' ? 'screening' : 'map-only' },
          metadata: {
            siteId: site.id,
            affectedSections: transition.affectedSections,
            facts: transition.facts,
            sourceFeature: transition.sourceFeature,
            snapshot: transition.snapshot,
          },
        });
      }
      toast.success(`Saved ${merged.length} layer${merged.length === 1 ? '' : 's'}`);
      onClose();
    } catch (e) {
      toast.error(`Failed to save layers: ${(e as Error).message}`);
      setSaving(false);
    }
  };

  const handleImportLandGate = async () => {
    if (landGateConnectionRequired) {
      requestLandGateConnectionDialog();
      return;
    }
    landGateRequestRef.current?.controller.abort();
    const request = {
      siteId: site.id,
      controller: new AbortController(),
    };
    landGateRequestRef.current = request;
    setLandGateLoading(true);
    try {
      const result = await fetchLandGateSiteLayers(site.id, site._ownerUid, {
        signal: request.controller.signal,
      });
      if (
        request.controller.signal.aborted
        || landGateRequestRef.current !== request
        || activeSiteIdRef.current !== request.siteId
      ) return;

      // Merge against the newest staged draft, not the render snapshot from
      // when the request began. This preserves edits made while import runs.
      const previous = draftLayersRef.current;
      const merge = mergeLandGateLayers(previous, result.layers);
      if (merge.added.length > 0 || merge.refreshed.length > 0) {
        applyBulk(merge.merged, previous);
      }
      const warning = result.warnings.length > 0 ? ` · ${result.warnings.join(' · ')}` : '';
      const reviewNotice = merge.reviewInvalidated.length > 0
        ? ` · ${merge.reviewInvalidated.length} changed feature${merge.reviewInvalidated.length === 1 ? '' : 's'} need re-review`
        : '';
      if (merge.added.length > 0 || merge.refreshed.length > 0) {
        const addedNotice = merge.added.length > 0
          ? `Added ${merge.added.length}`
          : '';
        const refreshedNotice = merge.refreshed.length > 0
          ? `${merge.added.length > 0 ? 'refreshed' : 'Refreshed'} ${merge.refreshed.length}`
          : '';
        const summary = [addedNotice, refreshedNotice].filter(Boolean).join(', ');
        toast.success(`${summary} LandGate layer${merge.added.length + merge.refreshed.length === 1 ? '' : 's'} (staged)${reviewNotice}${warning}`);
      } else {
        toast.info(`LandGate returned no new layers${warning}`);
      }
    } catch (error) {
      if (
        request.controller.signal.aborted
        || (error instanceof Error && error.name === 'AbortError')
      ) return;
      if (error instanceof LandGateConnectionError
        && (error.code === 'LANDGATE_CONNECTION_REQUIRED'
          || error.code === 'LANDGATE_CREDENTIAL_REJECTED')) {
        setLandGateConnectionRequired(true);
        requestLandGateConnectionDialog();
        toast.info('Connect LandGate to continue this import. Your staged map edits are preserved.');
        return;
      }
      toast.error((error as Error).message);
    } finally {
      if (landGateRequestRef.current === request) {
        landGateRequestRef.current = null;
        setLandGateLoading(false);
      }
    }
  };

  const handleCadFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;

    cadRequestRef.current?.controller.abort();
    const request = { siteId: site.id, controller: new AbortController() };
    cadRequestRef.current = request;
    setCadLoading(true);
    setCadPayload(null);
    setCadAlignment(null);
    setSelectedCadLayers(new Set());
    try {
      const payload = await importDwgFile(file, request.controller.signal);
      if (
        request.controller.signal.aborted
        || cadRequestRef.current !== request
        || activeSiteIdRef.current !== request.siteId
      ) return;
      setCadPayload(payload);
      setCadAlignment(defaultCadGeoreference(payload, scopeCenter));
      setSelectedCadLayers(new Set(payload.cadLayers.map(layer => layer.name)));
      const georefNote = payload.suggestedGeoreference?.projectedCrs?.code
        ? ` · ${payload.suggestedGeoreference.projectedCrs.code} detected`
        : payload.suggestedGeoreference
          ? ' · embedded alignment detected'
          : ' · alignment required';
      toast.success(`Read ${payload.features.length} editable CAD entities${georefNote}`);
    } catch (error) {
      if (request.controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      toast.error(error instanceof Error ? error.message : 'DWG import failed');
    } finally {
      if (cadRequestRef.current === request) {
        cadRequestRef.current = null;
        setCadLoading(false);
      }
    }
  };

  const handleUseSiteAnchor = () => {
    if (!cadPayload || !scopeCenter) {
      toast.error('This site has no centre to use as a CAD anchor.');
      return;
    }
    setCadAlignment(cadGeoreferenceAtSiteAnchor(cadPayload, scopeCenter));
  };

  const updateCadAlignment = (
    field: 'anchorLat' | 'anchorLng' | 'originX' | 'originY' | 'metersPerUnit' | 'rotationDeg',
    value: number,
  ) => {
    setCadAlignment(previous => previous ? {
      ...previous,
      [field]: value,
      source: 'manual',
      projectedCrs: undefined,
    } : previous);
  };

  const handleStageCadLayers = () => {
    if (!cadPayload || !cadAlignment) {
      toast.error('Set the CAD alignment before staging the overlay.');
      return;
    }
    if (selectedCadFeatureCount === 0) {
      toast.error('Select at least one CAD layer.');
      return;
    }
    if (selectedCadFeatureCount > MAX_CAD_ENTITIES_PER_IMPORT) {
      toast.error(`Select ${MAX_CAD_ENTITIES_PER_IMPORT} or fewer CAD entities per import to stay within site persistence limits.`);
      return;
    }
    try {
      const incoming = cadImportToSiteLayers(cadPayload, cadAlignment, selectedCadLayers);
      const previous = draftLayersRef.current;
      const merged = mergeCadImportedLayers(previous, incoming);
      if (merged.added.length === 0) {
        toast.info(`No new CAD entities to add · ${merged.alreadyPresent.length} already staged or saved`);
        return;
      }
      if (merged.merged.length > MAX_SITE_LAYERS_AFTER_CAD_IMPORT) {
        toast.error(`This import would create ${merged.merged.length} site layers. Select fewer CAD layers or remove old overlays first (maximum ${MAX_SITE_LAYERS_AFTER_CAD_IMPORT}).`);
        return;
      }
      applyBulk(merged.merged, previous);
      const skipped = merged.alreadyPresent.length
        ? ` · ${merged.alreadyPresent.length} already present`
        : '';
      toast.success(`Added ${merged.added.length} editable CAD entit${merged.added.length === 1 ? 'y' : 'ies'} (staged)${skipped}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'CAD staging failed');
    }
  };

  const handleExportCad = async () => {
    setCadExporting(true);
    try {
      const drawingCount = await downloadCadLayersDxf(draftLayersRef.current);
      toast.success(`Exported ${drawingCount} source-coordinate DXF${drawingCount === 1 ? '' : 's'} · insert into the original drawing to retain its georeference`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'CAD DXF export failed');
    } finally {
      setCadExporting(false);
    }
  };

  const updateSelected = (patch: Parameters<typeof patchSiteLayer>[2]) => {
    if (!selectedId) return;
    if (selectedLayerManaged) {
      toast.info('Published Generation & Impact layers are read-only here.');
      return;
    }
    setDraftLayers(prev => patchSiteLayer(prev, selectedId, patch));
  };

  // Per-row delete is now STAGED (was an immediate store write) so it shares
  // the same commit model as the bulk tools: it pushes an undo snapshot and
  // only persists on SAVE LAYERS. UNDO/CANCEL revert it.
  const deleteSelected = () => {
    if (!selectedId) return;
    if (selectedLayerManaged) {
      toast.info('Replace or unpublish this layer from the Generation & Impact workspace.');
      return;
    }
    applyBulk(removeSiteLayer(draftLayers, selectedId));
    toast.success('Layer removed (staged) · Undo to revert');
  };

  const closeEditor = () => {
    if (saving) return;
    landGateRequestRef.current?.controller.abort();
    landGateRequestRef.current = null;
    cadRequestRef.current?.controller.abort();
    cadRequestRef.current = null;
    setLandGateLoading(false);
    setCadLoading(false);
    onClose();
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (saving) return;
      if (drawMode !== null) {
        handleDrawCancel();
        return;
      }
      closeEditor();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTOR),
    ).filter(element => element.getAttribute('aria-hidden') !== 'true');
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current.focus();
      return;
    }

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const mapCenter = site.siteBoundaryAggregate?.aggregateCentroid
    ?? site.siteCentroid
    ?? site.location
    ?? { lat: 0, lng: 0 };
  const drawingActive = drawMode !== null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={saving ? undefined : closeEditor}
      data-testid="site-layer-editor"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-layer-editor-title"
        aria-busy={saving || landGateLoading || cadLoading || cadExporting}
        tabIndex={-1}
        className="bg-surface-1 w-[min(1200px,96vw)] max-h-[92vh] shadow-card border border-border flex flex-col rounded-lg overflow-hidden focus:outline-none"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <div className="px-5 py-3 border-b border-border flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 border border-border bg-state-hover flex items-center justify-center shrink-0">
            <Layers className="w-4 h-4 text-text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="site-layer-editor-title" className="type-display truncate">
              Edit map layers
            </h2>
            <p className="type-caption mt-0.5 truncate">
              {site.name} · {draftLayers.length} layer{draftLayers.length === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={closeEditor}
            disabled={saving}
            className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          <div className="relative flex-1 min-h-[280px] md:min-h-[420px] border-b md:border-b-0 md:border-r border-border overflow-hidden">
            <Map
              mapId="site-layer-editor-map"
              defaultCenter={mapCenter}
              defaultZoom={14}
              mapTypeId="hybrid"
              gestureHandling={drawingActive ? 'none' : 'cooperative'}
              mapTypeControl
              fullscreenControl
              streetViewControl={false}
            >
              <FitBoundsOnMount site={{ ...site, siteLayers: draftLayers }} parcels={siteParcels} />
              {site.location && (
                <AdvancedMarker position={site.location}>
                  <Pin
                    background={SITE_MAP_COLORS.markerBackground}
                    glyphColor={SITE_MAP_COLORS.markerGlyph}
                    borderColor={SITE_MAP_COLORS.markerBorder}
                  />
                </AdvancedMarker>
              )}
              {siteParcels.map(parcel => (
                <SiteBoundaryOverlay
                  key={parcel.id}
                  path={parcel.polygon}
                  strokeColor={SITE_MAP_COLORS.boundaryStroke}
                  fillOpacity={0.12}
                />
              ))}
              <SiteLayerOverlays layers={draftLayers} fillOpacity={0.25} />
              <SiteLayerDrawOverlay
                mode={drawMode}
                strokeColor={selectedColor}
                initialCoordinates={
                  (drawMode === 'edit-polygon' || drawMode === 'edit-line' || drawMode === 'edit-point') && selectedLayer
                    ? selectedLayer.geometry.coordinates
                    : undefined
                }
                onComplete={handleDrawComplete}
                onCancel={handleDrawCancel}
              />
            </Map>
          </div>

          <aside
            className="w-full md:w-[300px] shrink-0 flex flex-col max-h-[40vh] md:max-h-none overflow-hidden"
            data-testid="site-layer-editor-panel"
          >
            <div className="p-3 border-b border-border-subtle space-y-2">
              <p className="type-body text-text-muted uppercase tracking-widest">
                Site boundary is edited separately (Boundary button on overview).
              </p>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 rounded-md type-body uppercase tracking-widest"
                  disabled={drawingActive || saving}
                  onClick={() => startAddLayer('polygon')}
                  data-testid="layer-add-polygon"
                >
                  <Hexagon className="w-3 h-3 mr-1" /> Area
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 rounded-md type-body uppercase tracking-widest"
                  disabled={drawingActive || saving}
                  onClick={() => startAddLayer('line')}
                  data-testid="layer-add-line"
                >
                  <Spline className="w-3 h-3 mr-1" /> Line
                </Button>
              </div>
              {isLandGateImportEnabled() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full h-8 rounded-md type-section"
                  disabled={drawingActive || saving}
                  onClick={() => void handleImportLandGate()}
                  data-testid="landgate-import"
                >
                  {landGateLoading
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin motion-reduce:animate-none" aria-hidden />
                    : landGateConnectionRequired
                      ? <PlugZap className="w-3 h-3 mr-1" />
                      : <Download className="w-3 h-3 mr-1" />}
                  {landGateLoading
                    ? 'Restart LandGate import'
                    : landGateConnectionRequired
                      ? 'Connect LandGate'
                      : 'Import LandGate'}
                </Button>
              )}
            </div>

            <div
              className="p-3 border-b border-border-subtle space-y-2 max-h-[340px] overflow-y-auto"
              data-testid="cad-layer-import"
              aria-busy={cadLoading}
            >
              <input
                ref={cadInputRef}
                type="file"
                accept=".dwg,application/acad,application/x-acad,application/vnd.dwg"
                className="hidden"
                onChange={event => void handleCadFileSelected(event)}
                data-testid="cad-file-input"
              />
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <FileUp className="w-3.5 h-3.5 text-primary" aria-hidden />
                  <span className="type-section text-text-secondary">Editable DWG overlay</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 rounded-md type-meta"
                  disabled={drawingActive || saving || cadLoading}
                  onClick={() => cadInputRef.current?.click()}
                  data-testid="cad-file-choose"
                >
                  {cadLoading
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin motion-reduce:animate-none" aria-hidden />
                    : <FileUp className="w-3 h-3 mr-1" aria-hidden />}
                  {cadLoading ? 'Reading…' : 'Choose DWG'}
                </Button>
              </div>
              <p className="type-meta text-text-muted">
                Converts model-space planning geometry. The source DWG stays unchanged; DXF edits use its original coordinates for insertion back into that drawing.
              </p>

              {cadPayload && (
                <div className="space-y-2" data-testid="cad-import-review">
                  <div className="type-meta text-text-secondary">
                    <span className="font-bold">{cadPayload.fileName}</span>
                    {' · '}{cadPayload.dwgVersion}{' · '}{cadPayload.features.length} entities
                  </div>
                  {cadPayload.warnings.length > 0 && (
                    <ul className="space-y-1 border-l-2 border-warning pl-2" data-testid="cad-import-warnings">
                      {cadPayload.warnings.map((warning, index) => (
                        <li key={`${index}-${warning}`} className="type-meta text-text-muted">{warning}</li>
                      ))}
                    </ul>
                  )}

                  <details className="border border-border-subtle p-2">
                    <summary className="type-meta font-bold uppercase tracking-widest cursor-pointer">
                      CAD layers · {selectedCadLayers.size}/{cadPayload.cadLayers.length} selected
                    </summary>
                    <div className="flex gap-1 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 rounded-md type-meta"
                        onClick={() => setSelectedCadLayers(new Set(cadPayload.cadLayers.map(layer => layer.name)))}
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 rounded-md type-meta"
                        onClick={() => setSelectedCadLayers(new Set())}
                      >
                        Clear
                      </Button>
                    </div>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {cadPayload.cadLayers.map(layer => (
                        <label key={layer.name} className="flex items-center gap-2 type-meta text-text-secondary">
                          <input
                            type="checkbox"
                            checked={selectedCadLayers.has(layer.name)}
                            onChange={event => setSelectedCadLayers(previous => {
                              const next = new Set(previous);
                              if (event.target.checked) next.add(layer.name);
                              else next.delete(layer.name);
                              return next;
                            })}
                            className="w-3 h-3 accent-primary"
                          />
                          <span className="flex-1 truncate" title={layer.name}>{layer.name}</span>
                          <span>{layer.featureCount}</span>
                        </label>
                      ))}
                    </div>
                  </details>

                  {cadAlignment?.projectedCrs ? (
                    <div className="border border-success/40 bg-success/5 p-2 space-y-1">
                      <p className="type-meta font-bold text-text-secondary">
                        Embedded georeference · {cadAlignment.projectedCrs.code ?? cadAlignment.projectedCrs.name}
                      </p>
                      <p className="type-meta text-text-muted">
                        Embedded CAD projection and design/reference points will be used.
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 rounded-md type-meta"
                        onClick={() => setCadAlignment(previous => previous ? {
                          ...previous,
                          source: 'manual',
                          projectedCrs: undefined,
                        } : previous)}
                      >
                        Adjust manually
                      </Button>
                    </div>
                  ) : cadAlignment ? (
                    <div className="grid grid-cols-2 gap-1.5" data-testid="cad-manual-alignment">
                      <label className="type-meta text-text-muted">Anchor lat
                        <input type="number" step="any" value={cadAlignment.anchorLat} onChange={event => updateCadAlignment('anchorLat', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                      <label className="type-meta text-text-muted">Anchor lng
                        <input type="number" step="any" value={cadAlignment.anchorLng} onChange={event => updateCadAlignment('anchorLng', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                      <label className="type-meta text-text-muted">CAD origin X
                        <input type="number" step="any" value={cadAlignment.originX} onChange={event => updateCadAlignment('originX', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                      <label className="type-meta text-text-muted">CAD origin Y
                        <input type="number" step="any" value={cadAlignment.originY} onChange={event => updateCadAlignment('originY', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                      <label className="type-meta text-text-muted">Metres / unit
                        <input type="number" min="0" step="any" value={cadAlignment.metersPerUnit} onChange={event => updateCadAlignment('metersPerUnit', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                      <label className="type-meta text-text-muted">Rotation °
                        <input type="number" step="any" value={cadAlignment.rotationDeg} onChange={event => updateCadAlignment('rotationDeg', Number(event.target.value))} className="w-full px-1.5 py-1 border border-border bg-surface-1 font-mono text-text-primary" />
                      </label>
                    </div>
                  ) : (
                    <div className="border border-warning/50 p-2 space-y-1.5">
                      <p className="type-meta text-text-muted">
                        No supported embedded CRS. Alignment must be chosen explicitly and checked against a known control point.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full h-7 rounded-md type-meta"
                        disabled={!scopeCenter}
                        onClick={handleUseSiteAnchor}
                      >
                        Use site centre as drawing centre
                      </Button>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="w-full h-8 rounded-md type-section"
                    disabled={drawingActive || saving || cadLoading || !cadAlignment || selectedCadFeatureCount === 0 || selectedCadFeatureCount > MAX_CAD_ENTITIES_PER_IMPORT}
                    onClick={handleStageCadLayers}
                    data-testid="cad-stage-layers"
                  >
                    Stage {selectedCadFeatureCount} editable entit{selectedCadFeatureCount === 1 ? 'y' : 'ies'}
                  </Button>
                </div>
              )}
            </div>

            {/* Bulk layer-management toolbar — mass delete + radius/boundary
                scope. All actions are STAGED (pushed onto the undo stack);
                nothing persists until SAVE LAYERS. */}
            {(draftLayers.length > 0 || undoStack.length > 0) && (
              <div
                className="p-3 border-b border-border-subtle space-y-2"
                data-testid="layer-bulk-toolbar"
              >
                <div className="flex items-center justify-between">
                  <span className="type-meta font-bold uppercase tracking-widest text-text-muted">
                    Bulk tools
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 rounded-md type-meta uppercase tracking-widest disabled:opacity-30"
                    disabled={undoStack.length === 0 || drawingActive || saving}
                    onClick={handleUndo}
                    data-testid="layer-bulk-undo"
                  >
                    <Undo2 className="w-3 h-3 mr-1" /> Undo
                    {undoStack.length > 0 ? ` (${undoStack.length})` : ''}
                  </Button>
                </div>

                {managedLayerCount > 0 && (
                  <div
                    className="flex items-start gap-1.5 border-l-2 border-primary/60 pl-2 type-meta text-text-muted"
                    data-testid="layer-managed-bulk-note"
                  >
                    <LockKeyhole className="mt-0.5 h-3 w-3 shrink-0 text-primary" aria-hidden />
                    <span>
                      {managedLayerCount} published Generation &amp; Impact layer{managedLayerCount === 1 ? '' : 's'} protected from bulk changes.
                    </span>
                  </div>
                )}

                {editableDraftLayers.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="h-7 rounded-md type-meta uppercase tracking-widest"
                    disabled={drawingActive || saving}
                    onClick={() => void handleDeleteAll()}
                    data-testid="layer-bulk-delete-all"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> All ({editableDraftLayers.length})
                  </Button>
                  {layerTypeCounts.point > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md type-meta uppercase tracking-widest"
                      disabled={drawingActive || saving}
                      onClick={() => handleDeleteByType('point')}
                      data-testid="layer-bulk-delete-point"
                    >
                      Points ({layerTypeCounts.point})
                    </Button>
                  )}
                  {layerTypeCounts.line > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md type-meta uppercase tracking-widest"
                      disabled={drawingActive || saving}
                      onClick={() => handleDeleteByType('line')}
                      data-testid="layer-bulk-delete-line"
                    >
                      Lines ({layerTypeCounts.line})
                    </Button>
                  )}
                  {layerTypeCounts.polygon > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md type-meta uppercase tracking-widest"
                      disabled={drawingActive || saving}
                      onClick={() => handleDeleteByType('polygon')}
                      data-testid="layer-bulk-delete-area"
                    >
                      Areas ({layerTypeCounts.polygon})
                    </Button>
                  )}
                </div>
                )}

                {/* Radius scope */}
                {editableDraftLayers.length > 0 && (
                <div className="flex items-center gap-1">
                  <Scissors className="w-3 h-3 text-text-muted shrink-0" aria-hidden />
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={radiusValue}
                    onChange={e => setRadiusValue(e.target.value)}
                    disabled={drawingActive || saving}
                    className="w-14 type-body px-2 py-1 border border-border bg-surface-1 font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                    aria-label="Radius"
                    data-testid="layer-scope-radius-input"
                  />
                  <select
                    value={radiusUnit}
                    onChange={e => setRadiusUnit(e.target.value as RadiusUnit)}
                    disabled={drawingActive || saving}
                    className="type-meta px-1 py-1 border border-border bg-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                    aria-label="Radius unit"
                    data-testid="layer-scope-radius-unit"
                  >
                    <option value="km">km</option>
                    <option value="mi">mi</option>
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 rounded-md type-meta uppercase tracking-widest"
                    disabled={drawingActive || saving}
                    onClick={handleKeepWithinRadius}
                    data-testid="layer-scope-keep-radius"
                  >
                    Keep within
                  </Button>
                </div>
                )}

                {/* Boundary scope (only when a site boundary exists) */}
                {draftLayers.length > 0 && siteParcels.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full h-7 rounded-md type-meta uppercase tracking-widest"
                    disabled={drawingActive || saving}
                    onClick={handleKeepWithinBoundary}
                    data-testid="layer-scope-keep-boundary"
                  >
                    <Hexagon className="w-3 h-3 mr-1" /> Keep only within boundary
                  </Button>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {draftLayers.length === 0 && (
                <p className="type-meta px-2 py-4 text-center">
                  No layers yet · Add an area or line
                </p>
              )}
              {CATEGORY_ORDER.map(cat => {
                const inCat = draftLayers.filter(l => l.category === cat);
                if (inCat.length === 0) return null;
                return (
                  <div key={cat} className="space-y-0.5">
                    <div className="type-meta font-bold uppercase tracking-widest text-text-muted px-2 pt-2">
                      {CATEGORY_LABELS[cat]}
                    </div>
                    {inCat.map(layer => {
                      const active = layer.id === selectedId;
                      const dot = colorForCategory(layer.category, layer.color);
                      const managed = isManagedGenerationImpactLayer(layer);
                      return (
                        <button
                          key={layer.id}
                          type="button"
                          disabled={drawingActive}
                          onClick={() => setSelectedId(layer.id)}
                          aria-pressed={active}
                          data-testid={`layer-editor-row-${layer.id}`}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-left type-caption focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                            active ? 'bg-state-hover border-l-2 border-primary' : 'hover:bg-state-hover border-l-2 border-transparent'
                          }`}
                        >
                          <span
                            className="w-2.5 h-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: dot }}
                            aria-hidden
                          />
                          <span className="flex-1 truncate">{layer.name}</span>
                          <span className="type-meta text-text-muted uppercase">
                            {layer.geometry.type}
                          </span>
                          {layer.source === 'manual' && (
                            <span className="type-meta text-text-muted">manual</span>
                          )}
                          {layer.source === 'cad-import' && (
                            <span className="type-meta text-primary">CAD</span>
                          )}
                          {managed && (
                            <span
                              className="type-meta text-primary"
                              data-testid={`layer-managed-badge-${layer.id}`}
                            >
                              Managed
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {selectedLayer && !drawingActive && (
              <div className="border-t border-border-subtle p-3 space-y-2 shrink-0" data-testid="layer-editor-detail">
                {selectedLayerManaged && (
                  <div
                    className="flex items-start gap-2 border border-primary/30 bg-primary/5 p-2 type-meta text-text-secondary"
                    role="note"
                    data-testid="layer-managed-readonly-note"
                  >
                    <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <span>
                      Published layout layer · read-only here. Replace or unpublish it from Analyze / Generation &amp; Impact.
                    </span>
                  </div>
                )}
                <label className="block space-y-1">
                  <span className="type-body font-bold uppercase tracking-widest text-text-secondary">Name</span>
                  <input
                    type="text"
                    value={selectedLayer.name}
                    onChange={e => updateSelected({ name: e.target.value })}
                    disabled={selectedLayerManaged}
                    className="w-full type-body px-2 py-1.5 border border-border bg-surface-1 font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                    data-testid="layer-editor-name"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="type-body font-bold uppercase tracking-widest text-text-secondary">Category</span>
                  <select
                    value={selectedLayer.category}
                    onChange={e => updateSelected({ category: e.target.value as SiteLayerCategory })}
                    disabled={selectedLayerManaged}
                    className="w-full type-meta px-2 py-1.5 border border-border bg-surface-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                    data-testid="layer-editor-category"
                  >
                    {CATEGORY_ORDER.map(c => (
                      <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </label>
                <div className="space-y-1">
                  <span className="type-body font-bold uppercase tracking-widest text-text-secondary">Color</span>
                  <div className="flex flex-wrap gap-1">
                    {LAYER_COLOR_PRESETS.map(c => (
                      <button
                        key={c}
                        type="button"
                        aria-label={`Color ${c}`}
                        aria-pressed={selectedColor === c}
                        className={`w-5 h-5 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1 ${selectedColor === c ? 'ring-2 ring-primary ring-offset-1 ring-offset-surface-1' : 'border-border-subtle'}`}
                        style={{ backgroundColor: c }}
                        onClick={() => updateSelected({ color: c })}
                        disabled={selectedLayerManaged}
                      />
                    ))}
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={e => updateSelected({ color: e.target.value })}
                      disabled={selectedLayerManaged}
                      className="w-8 h-5 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                      data-testid="layer-editor-color"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 type-body uppercase tracking-widest">
                  <input
                    type="checkbox"
                    checked={selectedLayer.visible !== false}
                    onChange={e => updateSelected({ visible: e.target.checked })}
                    disabled={selectedLayerManaged}
                    className="w-3 h-3 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1"
                    data-testid="layer-editor-visible"
                  />
                  Visible on map
                </label>
                {!selectedLayerManaged && (
                  <LandGatePromotionControls
                    layer={selectedLayer}
                    onChange={next => setDraftLayers(prev => prev.map(layer => (
                      layer.id === next.id ? next : layer
                    )))}
                  />
                )}
                <div className="flex gap-1 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 rounded-md type-body uppercase tracking-widest"
                    onClick={startEditGeometry}
                    disabled={selectedLayerManaged}
                    data-testid="layer-editor-edit-geometry"
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit shape
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 rounded-md type-body uppercase tracking-widest text-destructive hover:text-destructive"
                    onClick={deleteSelected}
                    disabled={selectedLayerManaged}
                    data-testid="layer-editor-delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            <div
              className="border-t border-border p-3 grid grid-cols-2 gap-2 shrink-0"
              data-testid="layer-editor-footer"
            >
              {editableCadLayerCount > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="col-span-2 w-full h-8 rounded-md type-meta"
                  disabled={saving || drawingActive || cadExporting}
                  onClick={() => void handleExportCad()}
                  title="Exports source-coordinate geometry for insertion into the original DWG; the DXF does not carry the DWG GEODATA record."
                  data-testid="cad-export-dxf"
                >
                  {cadExporting
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin motion-reduce:animate-none" aria-hidden />
                    : <Download className="w-3 h-3 mr-1" aria-hidden />}
                  Source-coordinate DXF
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full h-8 rounded-md type-meta"
                onClick={closeEditor}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="w-full h-8 rounded-md type-meta"
                disabled={saving || drawingActive || landGateLoading || cadLoading || cadExporting}
                onClick={() => void handleSave()}
                data-testid="layer-editor-save"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-2 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
                Save layers
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
